import React, { useRef, useState } from "react";
import { Heart, X, Star, MapPin, RotateCcw } from "lucide-react";
import { rupiah } from "../lib/format";

export default function SwipeDeck({ items, saved, onSave, onOpen }) {
  const [idx, setIdx] = useState(0);
  const [drag, setDrag] = useState({ x: 0, active: false });
  const startX = useRef(0);
  const cur = items[idx];

  const finish = (dir) => {
    if (dir === "right" && cur) onSave(cur.id, true);
    setDrag({ x: 0, active: false });
    setIdx((i) => i + 1);
  };

  const down = (e) => { startX.current = e.clientX; setDrag({ x: 0, active: true }); };
  const move = (e) => { if (drag.active) setDrag((d) => ({ ...d, x: e.clientX - startX.current })); };
  const up = () => {
    if (Math.abs(drag.x) > 110) finish(drag.x > 0 ? "right" : "left");
    else setDrag({ x: 0, active: false });
  };

  if (!cur) {
    return (
      <div className="rounded-3xl border border-dashed border-[#e5d9ce] bg-[#fdfbf7] px-6 py-16 text-center">
        <p className="text-[15px] font-bold text-[#1e1b18]">Semua sudah kamu lihat 💕</p>
        <p className="mt-1 text-[13px] text-[#8a8278]">Yang kamu sukai ada di simpanan.</p>
        <button
          onClick={() => setIdx(0)}
          data-testid="swipe-restart"
          className="mx-auto mt-4 flex items-center gap-2 rounded-full bg-[#ffebeb] px-4 py-2 text-[13px] font-bold text-[#ff5a5f]"
        >
          <RotateCcw className="h-4 w-4" /> Ulangi
        </button>
      </div>
    );
  }

  const rot = drag.x * 0.05;
  const next = items[idx + 1];

  return (
    <div className="mx-auto max-w-sm">
      <div className="relative h-[440px]">
        {next && (
          <div className="absolute inset-0 scale-[0.94] overflow-hidden rounded-3xl border border-[#efeae4] bg-white opacity-70">
            <img src={next.img} alt="" className="h-2/3 w-full object-cover" />
          </div>
        )}
        <div
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerLeave={up}
          data-testid="swipe-card"
          className="absolute inset-0 cursor-grab overflow-hidden rounded-3xl border border-[#efeae4] bg-white kk-shadow active:cursor-grabbing"
          style={{
            transform: `translateX(${drag.x}px) rotate(${rot}deg)`,
            transition: drag.active ? "none" : "transform 0.3s cubic-bezier(0.16,1,0.3,1)",
            touchAction: "pan-y",
          }}
        >
          <div className="relative h-2/3">
            <img src={cur.img} alt={cur.name} className="h-full w-full object-cover" draggable={false} />
            <div className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-[#6b5f57]">{cur.category}</div>
            <div className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-black/45 px-2 py-1 text-[11px] font-bold text-white">
              <Star className="h-3 w-3 fill-[#ffcb5b] text-[#ffcb5b]" />{cur.rating}
            </div>
            {drag.x > 40 && (
              <div className="absolute left-4 top-16 -rotate-12 rounded-lg border-4 border-[#3fbf7f] px-3 py-1 text-lg font-black text-[#3fbf7f]">SIMPAN</div>
            )}
            {drag.x < -40 && (
              <div className="absolute right-4 top-16 rotate-12 rounded-lg border-4 border-[#c9363b] px-3 py-1 text-lg font-black text-[#c9363b]">LEWATI</div>
            )}
          </div>
          <div className="p-4">
            <div className="flex items-center gap-1 text-[12px] text-[#8a8278]">
              <MapPin className="h-3.5 w-3.5 text-[#c9878f]" />{cur.area}, {cur.city}
            </div>
            <h3 className="mt-1 text-[19px] font-extrabold">{cur.name}</h3>
            <p className="mt-1 line-clamp-2 text-[13px] text-[#8a8278]">{cur.desc}</p>
            <div className="mt-2 text-[15px] font-extrabold">{rupiah(cur.price)} <span className="text-[11px] font-medium text-[#a89f98]">/ berdua</span></div>
          </div>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-center gap-5">
        <button onClick={() => finish("left")} data-testid="swipe-skip"
          className="grid h-14 w-14 place-items-center rounded-full border border-[#efeae4] bg-white text-[#c9363b] kk-shadow-sm transition-transform active:scale-90">
          <X className="h-6 w-6" />
        </button>
        <button onClick={() => onOpen(cur)} data-testid="swipe-detail"
          className="rounded-full border border-[#efeae4] bg-white px-4 py-2.5 text-[13px] font-bold text-[#7c7277]">
          Detail
        </button>
        <button onClick={() => finish("right")} data-testid="swipe-save"
          className="grid h-14 w-14 place-items-center rounded-full kk-coral-gradient text-white kk-shadow-sm transition-transform active:scale-90">
          <Heart className="h-6 w-6 fill-white" />
        </button>
      </div>
    </div>
  );
}
